import React, { useState, useEffect } from 'react';
import { Offcanvas, Form, Button, Spinner, Card } from 'react-bootstrap';
import instance from "../../services/baseService";
import { toast } from "react-hot-toast";
import { ROUTES } from "../../constants/roles";
import { RxCross1 } from 'react-icons/rx';
import { extractErrorMessage } from '../../constants/common';
const CreateRoleOffCanvas = ({ show, onHide, onRoleCreated }) => {
    const [features, setFeatures] = useState([]);
    const [roleName, setRoleName] = useState("");
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (show) {
            fetchFeatures();
        }
    }, [show]);

    const fetchFeatures = async () => {
        setLoading(true);
        try {
            const res = await instance.get(ROUTES.RBAC_FEATURE_LIST);
            setFeatures(res.data?.data?.lists || []);
        } catch (err) {
            console.error('Error fetching features:', err);
            toast.error('Error fetching features', {
                position: 'top-center'
            });
        } finally {
            setLoading(false);
        }
    };

    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'roleName':
                if (!value.trim()) {
                    newErrors.roleName = 'Role name is required';
                } else if (value.length > 100) {
                    newErrors.roleName = 'Role name cannot exceed 100 characters';
                } else if (!/^[a-zA-Z\s]+$/.test(value)) {
                    newErrors.roleName = 'Role name can only contain letters and spaces';
                } else {
                    delete newErrors.roleName;
                }
                break;
            default:
                break;
        }

        setErrors(newErrors);
    };

    const validateRoleForm = () => {
        const newErrors = {};

        if (!roleName.trim()) {
            newErrors.roleName = "Role name is required.";
        } else if (roleName.length > 100) {
            newErrors.roleName = 'Role name cannot exceed 100 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(roleName)) {
            newErrors.roleName = 'Role name can only contain letters and spaces';
        }

        if (selectedPermissions.length === 0) {
            newErrors.permissions = "Select at least one permission.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleRoleNameChange = (value) => {
        setRoleName(value);
        validateField('roleName', value);
    };

    const handlePermissionChange = (codename) => {
        setSelectedPermissions((prev) => {
            const newPermissions = prev.includes(codename)
                ? prev.filter((item) => item !== codename)
                : [...prev, codename];

            // Clear permissions error if at least one permission is selected
            if (newPermissions.length > 0 && errors.permissions) {
                const newErrors = { ...errors };
                delete newErrors.permissions;
                setErrors(newErrors);
            }

            return newPermissions;
        });
    };

    const handleSelectAllGroup = (feature) => {
        const featurePermissions = feature.permissions.map(perm => perm.codename);
        const allSelected = featurePermissions.every(codename => selectedPermissions.includes(codename));

        if (allSelected) {
            // Deselect all permissions for this feature
            setSelectedPermissions(prev => prev.filter(codename => !featurePermissions.includes(codename)));
        } else {
            // Select all permissions for this feature
            setSelectedPermissions(prev => {
                const newPermissions = [...prev];
                featurePermissions.forEach(codename => {
                    if (!newPermissions.includes(codename)) {
                        newPermissions.push(codename);
                    }
                });

                // Clear permissions error if at least one permission is selected
                if (newPermissions.length > 0 && errors.permissions) {
                    const newErrors = { ...errors };
                    delete newErrors.permissions;
                    setErrors(newErrors);
                }

                return newPermissions;
            });
        }
    };

    const isAllGroupSelected = (feature) => {
        const featurePermissions = feature.permissions.map(perm => perm.codename);
        return featurePermissions.every(codename => selectedPermissions.includes(codename));
    };

    const handleRoleSubmit = async (e) => {
        e.preventDefault();
        if (!validateRoleForm()) return;

        setSubmitting(true);
        const payload = {
            role_name: roleName,
            permission_ids: selectedPermissions,
        };

        try {
            const response = await instance.post(ROUTES.RBAC_CREATE_ROLE, payload);
            if (response.data?.type === "success") {
                toast.success(response.data.message || "Role created successfully!");

                // Reset form
                setRoleName('');
                setSelectedPermissions([]);
                setErrors({});

                // Call callback if provided
                if (onRoleCreated) {
                    onRoleCreated();
                }

                // Close offcanvas
                onHide();
            }
        } catch (err) {
            const errorMessage = extractErrorMessage(err);
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        // Reset form when closing
        setRoleName('');
        setSelectedPermissions([]);
        setErrors({});
        onHide();
    };

    return (
        <Offcanvas
            show={show}
            onHide={handleClose}
            placement="end"
            className="!w-[52%]"
            style={{ maxWidth: '675px' }}
        >
            <Offcanvas.Header className="border-bottom">
                <div className="d-flex align-items-center justify-content-between w-100">
                    <Offcanvas.Title className="h4 text-dark !text-[24px] mb-0">
                        Create New Role
                    </Offcanvas.Title>
                    <Button
                        variant="link"
                        className="text-muted p-0 border-0 text-decoration-none"
                        onClick={handleClose}
                        style={{ fontSize: '1.5rem' }}
                    >
                        <RxCross1 />
                    </Button>
                </div>
            </Offcanvas.Header>

            <Offcanvas.Body className="overflow-auto">
                {loading ? (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Form onSubmit={handleRoleSubmit}>
                        {/* Role Name Input */}
                        <div className="mb-4">
                            <Form.Label className="fw-bold text-dark">Role Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={roleName}
                                onChange={(e) => handleRoleNameChange(e.target.value)}
                                placeholder="Enter role name"
                                isInvalid={!!errors.roleName}
                                maxLength={50}
                            />
                            {errors.roleName && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.roleName}
                                </Form.Control.Feedback>
                            )}
                        </div>

                        {/* Features & Permissions */}
                        <div className="p-4 border rounded">
                            <h5 className="!text-[20px] text-dark mb-3">Features & Permissions</h5>

                            {features.length > 0 ? (
                                <div className="space-y-4">
                                    {features.map((feature) => (
                                        <Card key={feature.id} className="border ">
                                            <Card.Body className="p-4">
                                                {/* Feature Header with Select All */}
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <h6 className="fw-bold text-dark mb-0">{feature.name}</h6>
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`select-all-${feature.id}`}
                                                        label="Select All"
                                                        checked={isAllGroupSelected(feature)}
                                                        onChange={() => handleSelectAllGroup(feature)}
                                                        className="text-sm text-muted"
                                                    />
                                                </div>

                                                {/* Permissions Grid */}
                                                <div className="row g-3">
                                                    {feature.permissions.map((perm) => (
                                                        <div key={perm.id} className="col-12 col-sm-6 col-md-4">
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={perm.codename}
                                                                label={perm.name}
                                                                checked={selectedPermissions.includes(perm.codename)}
                                                                onChange={() => handlePermissionChange(perm.codename)}
                                                                className="text-sm"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    No Features Found
                                </div>
                            )}

                            {errors.permissions && (
                                <div className="text-danger mt-2 small">
                                    {errors.permissions}
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        {features.length > 0 && (
                            <div className="d-flex justify-content-end gap-3 pt-4 mt-4">
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleClose}
                                    disabled={submitting}
                                    style={{ width: "100px" }}
                                    className='fw-bold'
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={submitting}
                                    style={{ width: "100px" }}
                                    className=" fw-bold"
                                >
                                    {submitting ? 'Saving...' : 'Save Role'}
                                </Button>
                            </div>
                        )}
                    </Form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default CreateRoleOffCanvas;